import { parseBooleanLike, pickFirstTrimmedString } from '../members/memberListUtils';
import {
  normalizeEventPerformanceTypeForThread,
  resolveExplicitEventChatMode,
  resolveExplicitEventGroupCount,
  resolveEventThreadId,
} from './eventThreadMetaUtils';

export const parseVisibilityFlag = (value) => parseBooleanLike(value);

export const normalizeGroupEventRows = (source, { includeArtistVisibility = false } = {}) => {
  const rows = Array.isArray(source) ? source : [];
  return rows
    .map((event) => {
      const normalized = {
        event_uuid: event?.event_uuid ? String(event.event_uuid) : '',
        name: pickFirstTrimmedString(event?.name),
        event_date: event?.event_date || null,
        open_time: event?.open_time || null,
        start_time: event?.start_time || null,
        venue_name: pickFirstTrimmedString(event?.venue_name, event?.venue, event?.place),
        venue: pickFirstTrimmedString(event?.venue, event?.place),
        prefecture_name: pickFirstTrimmedString(event?.prefecture_name, event?.prefecture),
        city_name: pickFirstTrimmedString(event?.city_name, event?.city),
        venue_address: pickFirstTrimmedString(event?.venue_address, event?.address),
        memo: pickFirstTrimmedString(event?.memo, event?.note, event?.remarks),
        is_primary: event?.is_primary === true || event?.is_group_root === true,
        chat_thread_id: resolveEventThreadId(event),
        chat_mode: resolveExplicitEventChatMode(event) ?? undefined,
        group_event_count: resolveExplicitEventGroupCount(event) ?? undefined,
        group_root_event_uuid:
          event?.group_root_event_uuid ??
          event?.groupRootEventUuid ??
          null,
        representative_event_uuid:
          event?.representative_event_uuid ??
          event?.representativeEventUuid ??
          null,
      };
      normalized.performance_type = normalizeEventPerformanceTypeForThread(
        normalized,
        pickFirstTrimmedString(event?.performance_type)
      );
      if (includeArtistVisibility) {
        normalized.members_visible_to_artists = parseVisibilityFlag(
          event?.members_visible_to_artists
        );
      }
      return normalized;
    })
    .filter((event) => event.event_uuid);
};

export const resolveEventVisibilityFromGroup = (groupEvents, eventId) => {
  if (!Array.isArray(groupEvents) || !eventId) return undefined;
  const matched = groupEvents.find(
    (event) => String(event?.event_uuid || '') === String(eventId)
  );
  if (!matched) return undefined;
  return parseVisibilityFlag(matched?.members_visible_to_artists);
};

export const mergeEventSidebarRow = ({
  detail,
  base,
  eventUuid,
  pickFirstStringFn = pickFirstTrimmedString,
  includeArtistVisibility = false,
} = {}) => {
  const normalizedEventUuid = String(
    eventUuid || detail?.event_uuid || base?.event_uuid || ''
  ).trim();
  if (!normalizedEventUuid) return null;

  const row = {
    ...(detail || {}),
    event_uuid: normalizedEventUuid,
    name: pickFirstStringFn(detail?.name, base?.name),
    event_date: detail?.event_date || base?.event_date || null,
    open_time: detail?.open_time || base?.open_time || null,
    start_time: detail?.start_time || base?.start_time || null,
    venue_name: pickFirstStringFn(
      detail?.venue_name,
      detail?.venue,
      detail?.place,
      base?.venue_name,
      base?.venue
    ),
    venue: pickFirstStringFn(detail?.venue, detail?.place, base?.venue),
    prefecture_name: pickFirstStringFn(
      detail?.prefecture_name,
      detail?.prefecture,
      base?.prefecture_name
    ),
    city_name: pickFirstStringFn(detail?.city_name, detail?.city, base?.city_name),
    venue_address: pickFirstStringFn(
      detail?.venue_address,
      detail?.address,
      base?.venue_address
    ),
    memo: pickFirstStringFn(detail?.memo, detail?.note, detail?.remarks, base?.memo),
    flyer_front_url: pickFirstStringFn(detail?.flyer_front_url, base?.flyer_front_url),
    flyer_back_url: pickFirstStringFn(detail?.flyer_back_url, base?.flyer_back_url),
    performance_flag: detail?.performance_flag ?? base?.performance_flag ?? null,
    use_existing_flyers:
      detail?.use_existing_flyers ?? base?.use_existing_flyers ?? false,
    original_event_uuid: detail?.original_event_uuid ?? base?.original_event_uuid ?? null,
    chat_thread_id:
      resolveEventThreadId(detail) ||
      resolveEventThreadId(base) ||
      null,
    chat_mode:
      resolveExplicitEventChatMode(detail) ??
      resolveExplicitEventChatMode(base) ??
      undefined,
    group_event_count:
      resolveExplicitEventGroupCount(detail) ??
      resolveExplicitEventGroupCount(base) ??
      undefined,
    group_root_event_uuid:
      detail?.group_root_event_uuid ??
      detail?.groupRootEventUuid ??
      base?.group_root_event_uuid ??
      base?.groupRootEventUuid ??
      null,
    representative_event_uuid:
      detail?.representative_event_uuid ??
      detail?.representativeEventUuid ??
      base?.representative_event_uuid ??
      base?.representativeEventUuid ??
      normalizedEventUuid,
  };
  row.performance_type = normalizeEventPerformanceTypeForThread(
    row,
    pickFirstStringFn(detail?.performance_type, base?.performance_type)
  );

  if (includeArtistVisibility) {
    const rawVisibility =
      detail?.members_visible_to_artists ?? base?.members_visible_to_artists;
    row.members_visible_to_artists = parseVisibilityFlag(rawVisibility);
  }

  return row;
};
